/**
 * Ghost Canvas Block Component
 *
 * E012-T013: Renders ghost nodes on the canvas.
 *
 * A ghost represents a database in another document that is referenced
 * via a relation. Users can:
 * - Move the ghost on the canvas (drag)
 * - Open the source document when they have access
 * - See relation edges terminating at this ghost
 */

import { I18n } from '@affine/i18n';
import { Bound } from '@blocksuite/global/gfx';
import {
  BlockComponent,
  FlavourExtension,
  toGfxBlockComponent,
} from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { html, nothing } from 'lit';
import { state } from 'lit/decorators.js';

import type { GhostBlockModel } from '../schemas/ghost-model.js';
import { GhostBlockSchema } from '../schemas/ghost-model.js';
import { applyPageModeFilter } from '../utils/page-mode-filter.js';
import * as styles from './ghost-canvas-block.css.js';

export const AFFINE_GHOST_CANVAS = 'affine-ghost-canvas';

function tGhost(key: string, fallback: string): string {
  const translated = I18n.t(key);
  return translated && translated !== key ? translated : fallback;
}

// =============================================================================
// Base Block Component (for non-canvas contexts - should not be used)
// =============================================================================

export class GhostBlockComponent extends BlockComponent<GhostBlockModel> {
  // IMPORTANT: Must use renderBlock() not render() for BlockComponent!
  // render() is used by the base class for wrapper logic
  override renderBlock() {
    // Ghost blocks should only exist on canvas - return nothing for doc view
    return nothing;
  }
}

// =============================================================================
// Canvas Block Component
// =============================================================================

export class GhostCanvasBlockComponent extends toGfxBlockComponent(
  GhostBlockComponent
) {
  @state()
  private accessor _editing = false;

  override connectedCallback(): void {
    super.connectedCallback();

    // E018-SPK021: Hide ghost nodes in page mode
    if (!applyPageModeFilter(this)) return;

    const selection = this.gfx.selection;

    this._editing = selection.has(this.model.id) && selection.editing;
    this._disposables.add(
      selection.slots.updated.subscribe(() => {
        this._editing = selection.has(this.model.id) && selection.editing;
      })
    );
  }

  // Note: Height is fixed by schema default (80px), no auto-calculation needed

  override renderGfxBlock() {
    const { model } = this;
    const title = model.displayTitle;
    const subtitle = model.displaySubtitle;
    const isInTrash = model.isTargetInTrash;
    const canOpenTargetDoc = model.canOpenTargetDoc;

    return html`
      <div class=${styles.ghostContainer} data-editing=${this._editing}>
        <ghost-canvas-background
          .editing=${this._editing}
          .ghost=${this.model}
        ></ghost-canvas-background>

        <div class=${styles.content}>
          <!-- Header with icon and title -->
          <div class=${styles.header}>
            <div class=${styles.iconWrapper}>
              <span class=${styles.icon}>${this._renderIcon()}</span>
            </div>
            <span class=${styles.title}>${title}</span>
          </div>

          <!-- Subtitle with doc info (clickable to navigate to doc) -->
          <div
            class="${styles.subtitle} ${this._isOtherDoc() && canOpenTargetDoc
              ? styles.subtitleClickable
              : ''}"
            @click=${this._handleNavigateToDoc}
          >
            <span class=${styles.subtitleIcon}>${this._renderDocIcon()}</span>
            <span class=${styles.subtitleText}>${subtitle}</span>
          </div>

          <!-- Action row: only render navigation actions for accessible targets -->
          <div class=${styles.actionRow}>
            ${isInTrash && canOpenTargetDoc
              ? html`
                  <button
                    class=${styles.trashIndicator}
                    style="cursor: pointer; background: none; border: none; font-family: inherit"
                    @click=${this._handleOpenTrash}
                  >
                    <span class=${styles.trashIcon}
                      >${this._renderTrashIcon()}</span
                    >
                    <span>
                      ${tGhost(
                        'com.streamlain.streamlain.ghost.in-trash',
                        'In Trash'
                      )}
                    </span>
                  </button>
                `
              : canOpenTargetDoc
                ? html`
                    <button
                      class=${styles.openButton}
                      @click=${this._handleOpenDoc}
                    >
                      <span class=${styles.openButtonIcon}
                        >${this._renderExternalLinkIcon()}</span
                      >
                      <span>
                        ${tGhost(
                          'com.streamlain.streamlain.ghost.open-document',
                          'Open Document'
                        )}
                      </span>
                    </button>
                  `
                : nothing}
          </div>
        </div>
      </div>
    `;
  }

  // ===========================================================================
  // Icons (inline SVG)
  // ===========================================================================

  /**
   * E018-S008-T008: Render icon based on ghost type
   */
  private _renderIcon() {
    const ghostType = this.model.props.ghostType || 'database';
    return ghostType === 'automation'
      ? this._renderAutomationIcon()
      : this._renderDatabaseIcon();
  }

  private _renderAutomationIcon() {
    // Play/automation icon (similar to workflow/play icon)
    return html`
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    `;
  }

  private _renderDatabaseIcon() {
    return html`
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      </svg>
    `;
  }

  private _renderDocIcon() {
    return html`
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
      </svg>
    `;
  }

  private _renderExternalLinkIcon() {
    return html`
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    `;
  }

  private _renderTrashIcon() {
    return html`
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="3 6 5 6 21 6" />
        <path
          d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
        />
      </svg>
    `;
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  /**
   * Check if the ghost points to a different doc than the current one.
   */
  private _isOtherDoc(): boolean {
    const docId = this.model.navigateToDocId;
    if (!this.model.canOpenTargetDoc) return false;
    if (!docId || docId === 'unknown') return false;
    const currentDocId = this.model.store?.id;
    return docId !== currentDocId;
  }

  /**
   * Navigate to the doc shown in the subtitle (only if it's a different doc).
   */
  private readonly _handleNavigateToDoc = (e: Event) => {
    if (!this._isOtherDoc()) return;
    e.stopPropagation();
    e.preventDefault();
    this._handleOpenDoc(e);
  };

  /**
   * Open the correct sidebar panel with trash section expanded.
   * Dispatches event to StreamLainSidebarSetup which clears activeBlock,
   * opens the workspace-level panel, and re-dispatches for trash expansion.
   */
  private readonly _handleOpenTrash = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();

    const ghostType = this.model.props.ghostType || 'database';

    window.dispatchEvent(
      new CustomEvent('ghost-open-trashcan', {
        detail: { blockId: this.model.id, ghostType },
      })
    );
  };

  private readonly _handleOpenDoc = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();

    if (!this.model.canOpenTargetDoc) return;

    const docId = this.model.navigateToDocId;
    if (!docId || docId === 'unknown') return;

    // Dispatch a bubbling custom event so the React shell can handle
    // SPA navigation (via workbench.openDoc) instead of a full page reload.
    this.dispatchEvent(
      new CustomEvent('navigate-to-doc', {
        detail: { docId },
        bubbles: true,
        composed: true,
      })
    );
  };
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_GHOST_CANVAS]: GhostCanvasBlockComponent;
  }
}

// =============================================================================
// Interaction Extension
// =============================================================================

export const GhostBlockInteraction =
  GfxViewInteractionExtension<GhostCanvasBlockComponent>(
    GhostBlockSchema.model.flavour,
    {
      // Allow resize to adjust width
      resizeConstraint: {
        minWidth: 180,
        maxWidth: 400,
        // Height is content-driven
      },

      handleRotate: () => ({
        beforeRotate(context) {
          context.set({ rotatable: false });
        },
      }),

      handleResize: ({ model }) => ({
        onResizeStart(context) {
          context.default(context);
        },
        onResizeMove(context) {
          const { newBound, constraint } = context;
          const { minWidth = 180, maxWidth = 400 } = constraint;

          // Only allow horizontal resize
          newBound.w = Math.max(minWidth, Math.min(newBound.w, maxWidth));

          // Keep original height (content-driven)
          const originalBound = Bound.deserialize(model.xywh);
          newBound.h = originalBound.h;

          model.xywh = newBound.serialize();
        },
        onResizeEnd(context) {
          context.default(context);
        },
      }),

      handleSelection: ({ gfx, model }) => ({
        onSelect(context) {
          const { selected, multiSelect } = context;
          const alreadySelected = gfx.selection.has(model.id);

          if (!multiSelect && selected && alreadySelected) {
            // Second click enables editing
            gfx.selection.set({
              elements: [model.id],
              editing: true,
            });
          } else {
            context.default(context);
          }
        },
      }),
    }
  );

// =============================================================================
// View Extension
// =============================================================================

export const GhostBlockViewExtension = FlavourExtension('affine:ghost');
